import { useState } from "react";
import { format } from "date-fns";
import { CalendarDays, ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SingleDateOption {
  id: string;
  label: string;
}

interface SingleDatePickerBaseProps {
  placeholder?: string;
  className?: string;
}

interface SingleDateOptionsProps extends SingleDatePickerBaseProps {
  /** Preset list variation: pick from a fixed set of dates (report versions, snapshots). */
  options: SingleDateOption[];
  value: string;
  onValueChange: (value: string) => void;
  date?: never;
  onDateChange?: never;
  disabledDate?: never;
}

interface SingleDateCalendarProps extends SingleDatePickerBaseProps {
  /** Calendar variation: pick any date from a month calendar. */
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabledDate?: (date: Date) => boolean;
  options?: never;
  value?: never;
  onValueChange?: never;
}

export type SingleDatePickerProps = SingleDateOptionsProps | SingleDateCalendarProps;

/**
 * Canonical SINGLE date selector (snapshots, report versions, form dates).
 * Uses the CalendarDays icon + caret. For ranges use <DateRangePicker />.
 *
 * Variations:
 *  - options list: pass `options` + `value` + `onValueChange`
 *  - calendar: pass `date` + `onDateChange` (+ optional `disabledDate`)
 */
export function SingleDatePicker(props: SingleDatePickerProps) {
  const { placeholder = "Select date", className } = props;
  const [open, setOpen] = useState(false);

  const isCalendar = "onDateChange" in props && typeof props.onDateChange === "function";
  const selectedOption = !isCalendar
    ? (props as SingleDateOptionsProps).options.find((o) => o.id === (props as SingleDateOptionsProps).value)
    : undefined;
  const calendarDate = isCalendar ? (props as SingleDateCalendarProps).date : undefined;

  const label = isCalendar
    ? calendarDate
      ? format(calendarDate, "MMM d, yyyy")
      : placeholder
    : selectedOption
      ? selectedOption.label
      : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 justify-start text-left font-normal gap-2",
            isCalendar && !calendarDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm">{label}</span>
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-auto bg-card border border-border shadow-lg z-[70]",
          isCalendar ? "p-0" : "min-w-[11rem] p-1.5"
        )}
        align="end"
        sideOffset={8}
      >
        {isCalendar ? (
          <Calendar
            mode="single"
            selected={calendarDate}
            onSelect={(d) => {
              (props as SingleDateCalendarProps).onDateChange(d);
              setOpen(false);
            }}
            disabled={(props as SingleDateCalendarProps).disabledDate}
            initialFocus
            className="p-3 pointer-events-auto"
          />
        ) : (
          (props as SingleDateOptionsProps).options.map((option) => {
            const active = option.id === (props as SingleDateOptionsProps).value;
            return (
              <button
                key={option.id}
                onClick={() => {
                  (props as SingleDateOptionsProps).onValueChange(option.id);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                  active ? "text-foreground font-medium" : "text-foreground hover:bg-primary/15"
                )}
              >
                <Check className={cn("h-4 w-4 shrink-0", active ? "opacity-100" : "opacity-0")} />
                {option.label}
              </button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
